import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

import useInterval from 'services/useInterval';

import { EllipsisText, formatDateTime } from '../Assessments/shared';
import { cancelJob, fetchJobs, fetchJobSummary, retryJob } from './api';
import { readableError } from './errors';
import { asNumber, asOption, useModuleFilters } from './filters';
import {
  JobActor,
  JobAttentionTarget,
  JobNeedsAttentionGroup,
  JobStatus,
  JobSummary,
  Pagination,
  ScenarioJob,
} from './types';

/**
 * The job ledger screen, and the vocabulary the rest of the control plane
 * renders jobs with. The status words, the outcome sentences, the polling
 * cadence and the target strip all live here so a job reads identically on
 * the Jobs tab, on a draft, and on a benchmark row.
 */

/* --------------------------- status vocabulary -------------------------- */

/** PENDING, BLOCKED and RUNNING are the states a job can still leave. */
export const JOB_LIVE_STATUSES = ['PENDING', 'BLOCKED', 'RUNNING'];

export const isJobLive = (status: JobStatus | undefined) =>
  JOB_LIVE_STATUSES.includes(String(status || '').toUpperCase());

export const JOB_KIND_LABELS: Record<string, string> = {
  'mint.preview': 'Mint preview',
  'mint.apply': 'Mint apply',
  'score.preview': 'Answer key preview',
  'benchmark.push': 'Answer key push',
  'benchmark.gate': 'Validity gate',
  'blueprint.publish': 'Publish version',
  'assessment.activate': 'Activate assessment',
  'registry.seed': 'Seed registry links',
  'curation.import': 'Import reviewed workbooks',
  // The answer-key redesign's two kinds. The words are the ledger's own
  // (`JobKind` on the server), so the row, the outcome panel and the job
  // detail page cannot end up calling the same job different things.
  'answer_key.derive': 'Derive answer key',
  'matrix.extend': 'Extend the matrix',
};

export const jobKindLabel = (kind: string) =>
  JOB_KIND_LABELS[kind] || kind || 'Job';

export const TARGET_KIND_LABELS: Record<string, string> = {
  scenario_draft: 'Draft',
  blueprint_version: 'Blueprint version',
  blueprint_variant: 'Blueprint variant',
  assessment_scenario: 'Answer key',
  registry: 'Registry',
};

/**
 * An actor as any of the three shapes the admin serializers may send it in.
 * A bare id is shown as an id rather than guessed at — "user 33" is a fact,
 * "admin" would be an invention.
 */
export const describeActor = (actor: JobActor | undefined): string => {
  if (actor == null || actor === '') return 'Not recorded';
  if (typeof actor === 'string') return actor;
  if (typeof actor === 'number') return `User ${actor}`;
  return (
    actor.username ||
    actor.name ||
    (actor.id ? `User ${actor.id}` : 'Not recorded')
  );
};

/**
 * REFUSED is amber, never red. Thirty-five of the fifty-eight curated
 * drafts refuse today for two named reasons with known remedies; painting
 * those the same colour as a crash teaches admins to ignore red.
 */
const STATUS_COLOR: Record<string, string> = {
  SUCCEEDED: 'green',
  REFUSED: 'gold',
  FAILED: 'red',
  LOST: 'default',
  CANCELED: 'default',
  RUNNING: 'processing',
  PENDING: 'blue',
  BLOCKED: 'default',
};

export const JobStatusTag: React.FC<{ status: JobStatus }> = ({ status }) => {
  const label = String(status || '').toUpperCase();
  return <Tag color={STATUS_COLOR[label] || 'default'}>{label || '—'}</Tag>;
};

/** The fallback only. The server writes a sentence per KIND on the row --
 *  what survived a worker's death is not the same fact for a preview, for an
 *  import that commits in one transaction, and for an encoding run whose
 *  cached encodings are durable and already paid for -- and this generic
 *  claim, shown for every kind, told admins nothing had been written when a
 *  blueprint version or a page of encodings had. */
export const LOST_JOB_COPY =
  'The worker stopped before this job finished, and the ledger holds no record of what it completed. Check what it was writing before running it again.';

export const BLOCKED_JOB_COPY =
  'Waiting for the scenario compute service to start. Nothing was sent to a worker; this job is released automatically when the service reports in.';

export type JobOutcome = {
  /** Drives the alert/tag colour. A refusal is a warning, not an error. */
  tone: 'success' | 'warning' | 'error' | 'info';
  title: string;
  detail: string;
  remedy: string;
  gate: string;
  /** True when the refusing gate offered a way to proceed. */
  confirmable: boolean;
};

/**
 * The one place a job's outcome is turned into words. A refusal is a named
 * gate that ran before the write and knows the remedy; a failure is an
 * exception no gate named; a loss is the absence of evidence. They must
 * read as three different things because they need three different actions.
 */
export const jobOutcome = (job: ScenarioJob): JobOutcome => {
  const status = String(job.status || '').toUpperCase();
  const blank = { detail: '', remedy: '', gate: '', confirmable: false };
  if (status === 'SUCCEEDED') {
    const made = (job.links || [])
      .map(link => link.label || link.rel)
      .filter(Boolean)
      .join(' · ');
    return { tone: 'success', title: 'Succeeded', ...blank, detail: made };
  }
  if (status === 'REFUSED') {
    const refusal = job.refusal || {};
    const gate = refusal.gate || refusal.code || '';
    return {
      tone: 'warning',
      title:
        refusal.message ||
        (gate
          ? `Refused by the ${gate} check.`
          : 'Refused before anything was written.'),
      detail: refusal.code ? `Reason code: ${refusal.code}` : '',
      remedy: refusal.remedy || '',
      gate,
      confirmable: Boolean(refusal.confirmable),
    };
  }
  if (status === 'FAILED') {
    const error = job.error || {};
    return {
      tone: 'error',
      title:
        error.message || 'The job raised an error that no gate anticipated.',
      detail: error.code ? `Error code: ${error.code}` : '',
      remedy: '',
      gate: '',
      confirmable: false,
    };
  }
  if (status === 'LOST') {
    const error = job.error || {};
    return {
      tone: 'error',
      // The row's own sentence first: it names what survived for THIS kind.
      title: error.message || LOST_JOB_COPY,
      ...blank,
    };
  }
  if (status === 'BLOCKED') {
    return { tone: 'warning', title: BLOCKED_JOB_COPY, ...blank };
  }
  if (status === 'CANCELED') {
    return {
      tone: 'info',
      title: 'Canceled by an admin before it ran.',
      ...blank,
    };
  }
  if (status === 'RUNNING') {
    return { tone: 'info', title: 'Running now.', ...blank };
  }
  return { tone: 'info', title: 'Waiting for a worker.', ...blank };
};

/** Cancelling is only legal before the job takes its locks. */
export const canCancelJob = (job: ScenarioJob) =>
  ['PENDING', 'BLOCKED'].includes(String(job.status || '').toUpperCase());

/** A retry is a new attempt of work that ended without doing it. A refusal
 *  is excluded on purpose: re-running an unchanged payload through the same
 *  gate produces the same refusal, so the remedy is the action, not a retry. */
export const canRetryJob = (job: ScenarioJob) =>
  ['FAILED', 'LOST', 'CANCELED'].includes(
    String(job.status || '').toUpperCase(),
  );

/* ------------------------------- clocks -------------------------------- */

export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const whole = Math.floor(seconds);
  if (whole < 60) return `${whole}s`;
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  if (minutes < 60) return `${minutes}:${String(rest).padStart(2, '0')}`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
};

export const jobDurationSeconds = (
  job: ScenarioJob,
  now: number,
): number | null => {
  if (!job.started_at) return null;
  const started = new Date(job.started_at).getTime();
  const ended = job.finished_at ? new Date(job.finished_at).getTime() : now;
  if (!Number.isFinite(started) || !Number.isFinite(ended)) return null;
  return (ended - started) / 1000;
};

/**
 * A wall clock that ticks locally, so an elapsed time on screen advances
 * between polls instead of jumping every few seconds. Lives beside the job
 * vocabulary because the job screens and the compute card both need the
 * same one.
 */
export const useNow = (ms = 1000, running = true) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms, running]);
  return now;
};

export const LIVE_POLL_MS = 3000;
export const IDLE_POLL_MS = 30000;

/**
 * The module's polling contract: three seconds while something is actually
 * moving, thirty when nothing is, and nothing at all while the tab is
 * hidden or the caller says the work is over. Deliberately not a socket —
 * this module sits outside the redux socket layer on purpose, and a screen
 * that shows nothing when the channel layer is down is worse than one that
 * asks again in three seconds.
 */
export const usePolling = (
  tick: () => void,
  { live, enabled = true }: { live: boolean; enabled?: boolean },
) => {
  const saved = useRef(tick);
  saved.current = tick;
  useInterval(
    () => {
      if (!enabled) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      saved.current();
    },
    live ? LIVE_POLL_MS : IDLE_POLL_MS,
  );
};

/* ---------------------------- needs attention --------------------------- */

/** How many targets one group offers before it says "and N more". */
const CHIP_LIMIT = 12;

/**
 * The targets a refusal group offers, in the shape the chips render.
 *
 * The server now sends `targets` with a kind per target. Older payloads sent
 * only `target_ids`, and the panel assumed every one of them was a draft:
 * that is what routed "Draft #181" (a blueprint version) and "Draft #0" (the
 * registry, which has no id at all) to a 404. The fallback keeps those
 * payloads renderable, and drops the ids that are not rows.
 */
export const attentionTargets = (
  group: JobNeedsAttentionGroup,
): JobAttentionTarget[] => {
  if (Array.isArray(group.targets)) {
    return group.targets.filter(
      target => target.id != null || Boolean(target.kind),
    );
  }
  return (group.target_ids || [])
    .filter(id => Boolean(id))
    .map(id => ({ kind: group.target_kind || 'scenario_draft', id }));
};

/** What a chip says: the kind of thing, and which one. */
export const attentionTargetLabel = (target: JobAttentionTarget): string => {
  const kind = TARGET_KIND_LABELS[target.kind] || target.kind || 'Target';
  if (target.id == null) return target.label || kind;
  return `${kind} #${target.id}`;
};

const NeedsAttention: React.FC<{
  groups: JobNeedsAttentionGroup[];
  onOpenTarget: (kind: string, id: number | null) => void;
}> = ({ groups, onOpenTarget }) => {
  if (!groups.length) return null;
  return (
    <Card
      className="assessment-summary"
      size="small"
      title="Needs attention"
      data-testid="jobs-needs-attention"
    >
      <Typography.Paragraph type="secondary">
        Every refusal below was raised by a named check before anything was
        written. Each group is one reason with one remedy, not one incident per
        target.
      </Typography.Paragraph>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {groups.map(group => (
          <Alert
            key={group.code}
            type="warning"
            showIcon
            title={`${group.code} — ${group.count} ${
              group.count === 1 ? 'job' : 'jobs'
            }`}
            description={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {group.gate && (
                  <Typography.Text type="secondary">
                    Raised by {group.gate}.
                  </Typography.Text>
                )}
                {group.remedy && <span>{group.remedy}</span>}
                {Boolean(attentionTargets(group).length) && (
                  <Space size={[6, 6]} wrap className="assessment-tag-group">
                    {attentionTargets(group)
                      .slice(0, CHIP_LIMIT)
                      /* Keyed by position, not by id: one code can name the
                         same id under two kinds, and duplicate keys were the
                         module's only console error. */
                      .map((target, index) => (
                        <Typography.Link
                          key={index}
                          onClick={() => onOpenTarget(target.kind, target.id)}
                        >
                          {attentionTargetLabel(target)}
                        </Typography.Link>
                      ))}
                    {attentionTargets(group).length > CHIP_LIMIT && (
                      <Typography.Text type="secondary">
                        and {attentionTargets(group).length - CHIP_LIMIT} more
                      </Typography.Text>
                    )}
                  </Space>
                )}
              </Space>
            }
          />
        ))}
      </Space>
    </Card>
  );
};

/* ------------------------------ the strip ------------------------------ */

const STRIP_LIMIT = 5;

/**
 * The last few jobs on one target, for the screens that own that target
 * (a draft, a benchmark row, the registry). Mounted by the action screens;
 * it navigates to the full job rather than duplicating the detail.
 */
export const JobsStrip: React.FC<{
  target_kind: string;
  target_id: number;
  onOpen?: (id: string) => void;
}> = ({ target_kind, target_id, onOpen }) => {
  const history = useHistory();
  const [rows, setRows] = useState<ScenarioJob[]>([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await fetchJobs({
        target_kind,
        target_id,
        page_size: STRIP_LIMIT,
      });
      setRows(response.results);
      setError('');
    } catch (requestError) {
      setError(readableError(requestError, { what: 'These jobs' }));
    }
  }, [target_kind, target_id]);

  useEffect(() => {
    void load();
  }, [load]);

  usePolling(load, { live: rows.some(row => isJobLive(row.status)) });

  const open = (id: string) =>
    onOpen
      ? onOpen(id)
      : history.push(
          `/dashboard/scenario-builder?job=${encodeURIComponent(id)}`,
        );

  if (error) {
    return (
      <Alert
        className="assessment-callout"
        type="error"
        showIcon
        title={error}
      />
    );
  }
  if (!rows.length) {
    return (
      <Typography.Text type="secondary" data-testid="jobs-strip-empty">
        No jobs have run against this target.
      </Typography.Text>
    );
  }
  return (
    <Space
      direction="vertical"
      size={4}
      style={{ width: '100%' }}
      data-testid="jobs-strip"
    >
      {rows.map(job => {
        const outcome = jobOutcome(job);
        return (
          <Space key={job.id} size={8} wrap className="assessment-tag-group">
            <JobStatusTag status={job.status} />
            <Typography.Link onClick={() => open(job.id)}>
              {jobKindLabel(job.kind)}
            </Typography.Link>
            <Typography.Text type="secondary">
              {formatDateTime(job.created_at)} · {describeActor(job.created_by)}
            </Typography.Text>
            {outcome.tone !== 'success' && (
              <Typography.Text type="secondary">
                {outcome.title}
              </Typography.Text>
            )}
          </Space>
        );
      })}
    </Space>
  );
};

/* ------------------------------ the screen ----------------------------- */

const STATUS_OPTIONS = [
  'PENDING',
  'BLOCKED',
  'RUNNING',
  'SUCCEEDED',
  'REFUSED',
  'FAILED',
  'LOST',
  'CANCELED',
].map(value => ({ value, label: value }));

const KIND_OPTIONS = Object.entries(JOB_KIND_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const TRIGGER_OPTIONS = [
  { value: 'manual', label: 'Requested by an admin' },
  { value: 'scheduled', label: 'Raised by the hourly sweep' },
  { value: 'chain', label: 'Chained from an earlier job' },
];

const EMPTY_PAGINATION: Pagination = {
  count: 0,
  total_pages: 1,
  page: 1,
  page_size: 25,
  next_page: null,
  previous_page: null,
};

type Props = {
  onOpen: (id: string) => void;
  onOpenTarget?: (kind: string, id: number | null) => void;
  onOpenCompute?: () => void;
};

const Jobs: React.FC<Props> = ({ onOpen, onOpenTarget, onOpenCompute }) => {
  const [rows, setRows] = useState<ScenarioJob[]>([]);
  const [pagination, setPagination] = useState<Pagination>(EMPTY_PAGINATION);
  const [summary, setSummary] = useState<JobSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [page, setPage] = useState(1);
  // The filters ride in the address bar: a chip elsewhere in the module opens
  // this ledger already scoped to one target ("every job about blueprint
  // version 912"), which is the only destination for a target that has no
  // screen of its own — and an admin who filters, opens a job and comes back
  // finds the filter still on.
  const [filters, setFilters] = useModuleFilters('jobs', {
    status: '',
    kind: '',
    trigger: '',
    target_kind: '',
    target_id: '',
  });
  const status = asOption(filters.status);
  const kind = asOption(filters.kind);
  const trigger = asOption(filters.trigger);
  const targetKind = asOption(filters.target_kind);
  const targetId = asNumber(filters.target_id);

  const load = useCallback(
    async (showSpinner = false) => {
      if (showSpinner) setLoading(true);
      try {
        const [ledger, counts] = await Promise.all([
          fetchJobs({
            page,
            status,
            kind,
            trigger,
            target_kind: targetKind,
            target_id: targetId,
          }),
          fetchJobSummary(),
        ]);
        setRows(ledger.results);
        setPagination(ledger.pagination);
        setSummary(counts);
        setError('');
      } catch (requestError) {
        setError(readableError(requestError, { what: 'The job ledger' }));
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [page, status, kind, trigger, targetKind, targetId],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  const live =
    rows.some(row => isJobLive(row.status)) || Boolean(summary && summary.live);
  usePolling(() => void load(), { live });

  const now = useNow(
    1000,
    rows.some(row => row.status === 'RUNNING'),
  );

  const runCommand = async (
    job: ScenarioJob,
    command: (id: string) => Promise<ScenarioJob>,
  ) => {
    setBusyId(job.id);
    setActionError('');
    try {
      await command(job.id);
      await load();
    } catch (commandError) {
      setActionError(readableError(commandError, { what: 'That job' }));
    } finally {
      setBusyId('');
    }
  };

  const openTarget = (targetKind: string, id: number | null) => {
    if (onOpenTarget) onOpenTarget(targetKind, id);
  };

  const columns: any[] = [
    {
      title: 'Job',
      fixed: 'left',
      width: 210,
      render: (_: unknown, job: ScenarioJob) => (
        <Space direction="vertical" size={0}>
          <Typography.Link onClick={() => onOpen(job.id)}>
            {jobKindLabel(job.kind)}
          </Typography.Link>
          <Space size={4} wrap className="assessment-tag-group">
            <EllipsisText code value={String(job.id).slice(0, 8)} />
            {(job.attempt || 1) > 1 && <Tag>ATTEMPT {job.attempt}</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Target',
      width: 220,
      render: (_: unknown, job: ScenarioJob) => (
        <Space direction="vertical" size={0}>
          {job.target_kind === 'registry' || !job.target_id ? (
            <Typography.Text>
              {job.target_label || TARGET_KIND_LABELS[job.target_kind] || '—'}
            </Typography.Text>
          ) : (
            <Typography.Link
              onClick={() => openTarget(job.target_kind, job.target_id)}
            >
              <span className="assessment-ellipsis">
                {job.target_label ||
                  `${TARGET_KIND_LABELS[job.target_kind] || job.target_kind} #${job.target_id}`}
              </span>
            </Typography.Link>
          )}
          <EllipsisText
            code
            value={
              job.target_id
                ? `${job.target_kind}:${job.target_id}`
                : job.target_kind
            }
          />
        </Space>
      ),
    },
    {
      title: 'Requested by',
      width: 170,
      render: (_: unknown, job: ScenarioJob) => (
        <Space direction="vertical" size={0}>
          <EllipsisText value={describeActor(job.created_by)} />
          {job.acts_as &&
            describeActor(job.acts_as) !== describeActor(job.created_by) && (
              <Typography.Text type="secondary">
                acting as {describeActor(job.acts_as)}
              </Typography.Text>
            )}
        </Space>
      ),
    },
    {
      title: 'Raised',
      dataIndex: 'trigger',
      width: 120,
      render: (value: string) => (
        <Tooltip
          title={
            TRIGGER_OPTIONS.find(option => option.value === value)?.label ||
            'Trigger not recorded'
          }
        >
          <Tag>{String(value || 'manual').toUpperCase()}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Lane',
      dataIndex: 'lane',
      width: 100,
      render: (value: string) => (
        <Tag color={value === 'heavy' ? 'purple' : 'default'}>
          {String(value || 'slim').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (value: JobStatus) => <JobStatusTag status={value} />,
    },
    {
      title: 'Started',
      width: 190,
      className: 'assessment-cell-nowrap',
      render: (_: unknown, job: ScenarioJob) => (
        <Space direction="vertical" size={0}>
          <span>{formatDateTime(job.started_at || job.created_at)}</span>
          <Typography.Text type="secondary">
            {formatDuration(jobDurationSeconds(job, now))}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Outcome',
      width: 380,
      render: (_: unknown, job: ScenarioJob) => {
        const outcome = jobOutcome(job);
        return (
          <Space direction="vertical" size={2}>
            <Typography.Text
              type={
                outcome.tone === 'error'
                  ? 'danger'
                  : outcome.tone === 'warning'
                    ? 'warning'
                    : undefined
              }
            >
              {outcome.title}
            </Typography.Text>
            {outcome.gate && (
              <EllipsisText code value={`gate: ${outcome.gate}`} />
            )}
            {outcome.remedy && (
              <Typography.Text type="secondary">
                {outcome.remedy}
              </Typography.Text>
            )}
            {String(job.status).toUpperCase() === 'BLOCKED' &&
              onOpenCompute && (
                <Typography.Link onClick={onOpenCompute}>
                  Open the compute service
                </Typography.Link>
              )}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 200,
      render: (_: unknown, job: ScenarioJob) => (
        <Space size={4}>
          <Button size="small" onClick={() => onOpen(job.id)}>
            Open
          </Button>
          {canCancelJob(job) && (
            <Button
              size="small"
              loading={busyId === job.id}
              onClick={() => runCommand(job, cancelJob)}
            >
              Cancel
            </Button>
          )}
          {canRetryJob(job) && (
            <Button
              size="small"
              loading={busyId === job.id}
              onClick={() => runCommand(job, retryJob)}
            >
              Run again
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Job ledger"
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => load(true)}
          loading={loading}
        >
          Refresh
        </Button>
      }
    >
      <Alert
        className="assessment-callout"
        showIcon
        type="info"
        title="Every action taken through this admin is recorded here"
        description="A job that was refused was stopped by a named check before anything was written, and the check states its remedy. A job that failed raised an error nothing anticipated. A job that was lost had its worker stop mid-run, so its transaction rolled back and nothing was written at all."
      />
      {summary && (
        <NeedsAttention
          groups={summary.needs_attention || []}
          onOpenTarget={openTarget}
        />
      )}
      <Space className="assessment-filter-row" wrap>
        <Select
          allowClear
          aria-label="Job status"
          placeholder="All statuses"
          style={{ width: 180 }}
          value={status}
          onChange={value => {
            setPage(1);
            setFilters({ status: value || '' });
          }}
          options={STATUS_OPTIONS}
        />
        <Select
          allowClear
          aria-label="Job kind"
          placeholder="All job kinds"
          style={{ width: 230 }}
          value={kind}
          onChange={value => {
            setPage(1);
            setFilters({ kind: value || '' });
          }}
          options={KIND_OPTIONS}
        />
        <Select
          allowClear
          aria-label="How the job was raised"
          placeholder="Raised by anything"
          style={{ width: 250 }}
          value={trigger}
          onChange={value => {
            setPage(1);
            setFilters({ trigger: value || '' });
          }}
          options={TRIGGER_OPTIONS}
        />
        {targetKind && (
          <Tag
            closable
            data-testid="jobs-target-scope"
            onClose={() => {
              setPage(1);
              setFilters({ target_kind: '', target_id: '' });
            }}
          >
            {`Only ${TARGET_KIND_LABELS[targetKind] || targetKind}`}
            {targetId != null ? ` #${targetId}` : ''}
          </Tag>
        )}
      </Space>
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      {actionError && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={actionError}
        />
      )}
      <Table
        rowKey="id"
        className="assessment-table"
        tableLayout="fixed"
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 1710 }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.page_size,
          total: pagination.count,
          showSizeChanger: false,
          onChange: setPage,
          showTotal: total => `${total} job${total === 1 ? '' : 's'}`,
        }}
      />
    </Card>
  );
};

export default Jobs;
